import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  Tooltip,
  IconButton,
  alpha,
  useTheme,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  CalendarToday as CalendarIcon,
  FitnessCenter as FitnessIcon,
  Science as ScienceIcon,
  Restaurant as NutritionIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Groups as GroupsIcon,
  LocalFireDepartment as FireIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { managedLocations, whereLocation } from '../auth/managedLocations';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../shared/utils/firebase';
import { format, parseISO, differenceInWeeks } from 'date-fns';
import LoadingContainer from '../../components/common/LoadingContainer';
import CustomLoader from '../../components/common/CustomLoader';

interface VisitHistoryItem {
  session_date: string;
  service_type: string;
  service_category: string;
  pricing_option?: string;
  staff_name?: string;
  location_name?: string;
  duration_minutes?: number;
  late_cancel?: boolean;
  no_show?: boolean;
}

interface IgnitionClientData {
  id: string;
  client_id: string;
  client_name: string;
  lead_coach_name: string;
  lead_coach_id: string;
  primary_location: string;
  current_programme: string;
  health_consult_date: string | null;
  first_session_date: string | null;
  pt_sessions: number;
  metabolic_screenings: number;
  physical_performance_screenings: number;
  nutrition_sessions: number;
  ignition_expiration_date: string | null;
  visit_history?: VisitHistoryItem[];
}

// Removed deprecated motion() usage - use motion.div with Card/Box styling instead

const IgnitionOverview: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userRole, userMetadata } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<IgnitionClientData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [coachFilter, setCoachFilter] = useState<string>('');
  const [programmeFilter, setProgrammeFilter] = useState<string>('');
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);
  const [availableCoaches, setAvailableCoaches] = useState<{id: string, name: string}[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [activeFilters, setActiveFilters] = useState(0);

  // Determine programme type
  const getProgrammeType = (programme: string): string => {
    if (!programme) return 'N/A';
    const programmeLC = programme.toLowerCase();
    
    if (programmeLC.includes('max')) {
      return 'Max';
    } else if (programmeLC.includes('promo')) {
      return 'Promo';
    } else {
      return 'Regular';
    }
  };

  // Calculate statistics
  const statistics = useMemo(() => {
    const now = new Date();
    let activeClients = 0;
    let inactiveClients = 0;
    let maxClients = 0;
    let promoClients = 0;
    let regularClients = 0;

    clients.forEach(client => {
      const type = getProgrammeType(client.current_programme);
      
      if (type === 'Max') {
        maxClients++;
      } else if (type === 'Promo') {
        promoClients++;
      } else {
        regularClients++;
      }
      
      if (client.ignition_expiration_date) {
        try {
          const expDate = parseISO(client.ignition_expiration_date);
          if (expDate >= now) {
            activeClients++;
          } else {
            inactiveClients++;
          }
        } catch (error) {
          console.error('Error parsing date:', error);
        }
      }
    });

    return {
      total: clients.length,
      active: activeClients,
      inactive: inactiveClients,
      max: maxClients,
      promo: promoClients,
      regular: regularClients
    };
  }, [clients]);

  // Format date strings
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    try {
      const date = parseISO(dateString);
      return format(date, 'MMM dd, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Find the first session date from visit history
  const findFirstSessionDate = (visitHistory?: VisitHistoryItem[], currentProgramme?: string): string | null => {
    if (!visitHistory || !visitHistory.length || !currentProgramme) return null;
    
    const matchingVisits = visitHistory.filter(visit => 
      visit.pricing_option && 
      currentProgramme && 
      visit.pricing_option.toLowerCase().includes(currentProgramme.toLowerCase())
    );
    
    if (!matchingVisits.length) return null;
    
    matchingVisits.sort((a, b) => {
      const dateA = new Date(a.session_date).getTime();
      const dateB = new Date(b.session_date).getTime();
      return dateA - dateB;
    });
    
    return matchingVisits[0].session_date;
  };

  // Calculate weeks since first session
  const calculateWeeksSinceFirstSession = (firstSessionDate: string | null): number => {
    if (!firstSessionDate) return 0;
    try {
      const date = parseISO(firstSessionDate);
      return differenceInWeeks(new Date(), date);
    } catch (error) {
      console.error('Error calculating weeks:', error);
      return 0;
    }
  };

  // Calculate weeks until expiration
  const calculateWeeksUntilExpiration = (expirationDate: string | null): number => {
    if (!expirationDate) return 0;
    try {
      const date = parseISO(expirationDate);
      return differenceInWeeks(date, new Date());
    } catch (error) {
      console.error('Error calculating weeks until expiration:', error);
      return 0;
    }
  };

  // Normalize client name from "Lastname, Firstname" to "Firstname Lastname"
  const normalizeClientName = (name: string): string => {
    if (!name) return '';
    if (name.includes(',')) {
      const [lastName, firstName] = name.split(',').map(part => part.trim());
      return `${firstName} ${lastName}`;
    }
    return name;
  };

  const getProgrammeNumber = (programme: string): string => {
    if (!programme) return '';
    const match = programme.match(/(\d+)\s*PT/);
    return match ? match[1] : '';
  };

  // Fetch ignition clients
  const fetchIgnitionClients = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const clientsRef = collection(db, 'bigquery_cache/client_data/comprehensive_details');
      let clientQuery;
      
      const managerLocations = managedLocations(userMetadata);
      if (userRole === 'MANAGER' && managerLocations.length > 0) {
        clientQuery = query(
          clientsRef,
          whereLocation('primary_location', managerLocations),
          where('current_programme', '>=', 'Ignition'),
          where('current_programme', '<=', 'Ignition\uf8ff')
        );
      } else if (userRole === 'ADMIN') {
        clientQuery = query(
          clientsRef,
          where('current_programme', '>=', 'Ignition'),
          where('current_programme', '<=', 'Ignition\uf8ff')
        );
      } else if (userRole === 'USER' && userMetadata?.coach_id) {
        clientQuery = query(
          clientsRef,
          where('lead_coach_id', '==', userMetadata.coach_id),
          where('current_programme', '>=', 'Ignition'),
          where('current_programme', '<=', 'Ignition\uf8ff')
        );
      } else {
        setClients([]);
        setLoading(false);
        return;
      }
      
      const querySnapshot = await getDocs(clientQuery);
      const clientsData: IgnitionClientData[] = [];
      const locations = new Set<string>();
      const coaches = new Set<{id: string, name: string}>();
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        if (data.current_programme && typeof data.current_programme === 'string' && 
            data.current_programme.toLowerCase().includes('ignition')) {
          const visitHistory = data.visit_history || [];
          const firstSessionFromVisits = findFirstSessionDate(visitHistory, data.current_programme);
          
          const clientData: IgnitionClientData = {
            id: doc.id,
            client_id: data.client_id || '',
            client_name: data.client_name || '',
            lead_coach_name: data.lead_coach_name || '',
            lead_coach_id: data.lead_coach_id || '',
            primary_location: data.primary_location || '',
            current_programme: data.current_programme || '',
            health_consult_date: data.health_consult_date || null,
            first_session_date: firstSessionFromVisits || data.first_session_date || null,
            pt_sessions: data.pt_sessions || 0,
            metabolic_screenings: data.metabolic_screenings || 0,
            physical_performance_screenings: data.physical_performance_screenings || 0,
            nutrition_sessions: data.nutrition_sessions || 0,
            ignition_expiration_date: data.ignition_expiration_date || null,
            visit_history: visitHistory
          };
          
          clientsData.push(clientData);
          
          if (data.primary_location) {
            locations.add(data.primary_location);
          }
          
          if (data.lead_coach_id && data.lead_coach_name) {
            coaches.add({id: data.lead_coach_id, name: data.lead_coach_name});
          }
        }
      });
      
      setClients(clientsData);
      setAvailableLocations(Array.from(locations));
      setAvailableCoaches(Array.from(coaches));
      
    } catch (err) {
      console.error('Error fetching ignition clients:', err);
      setError('Failed to fetch ignition clients. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Filter clients based on search and filters
  const filteredClients = useMemo(() => {
    let filtered = [...clients];
    let filterCount = 0;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(client => 
        normalizeClientName(client.client_name).toLowerCase().includes(term) ||
        client.client_id.toLowerCase().includes(term)
      );
      filterCount++;
    }
    
    if (locationFilter) {
      filtered = filtered.filter(client => client.primary_location === locationFilter);
      filterCount++;
    }
    
    if (coachFilter) {
      filtered = filtered.filter(client => client.lead_coach_id === coachFilter);
      filterCount++;
    }
    
    if (programmeFilter) {
      if (programmeFilter === 'Max') {
        filtered = filtered.filter(client => 
          client.current_programme.toLowerCase().includes('max')
        );
      } else if (programmeFilter === 'Regular') {
        filtered = filtered.filter(client => 
          !client.current_programme.toLowerCase().includes('max')
        );
      }
      filterCount++;
    }
    
    setActiveFilters(filterCount);
    return filtered;
  }, [clients, searchTerm, locationFilter, coachFilter, programmeFilter]);

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setLocationFilter('');
    setCoachFilter('');
    setProgrammeFilter('');
  };

  // DataGrid columns definition
  const columns: GridColDef[] = [
    {
      field: 'client_name',
      headerName: 'Client',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Stack>
            <Typography variant="body2" fontWeight={600}>
              {normalizeClientName(params.value as string)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {params.row.client_id}
            </Typography>
          </Stack>
        </motion.div>
      ),
    },
    {
      field: 'lead_coach_name',
      headerName: 'Coach',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {normalizeClientName(params.value as string)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'current_programme',
      headerName: 'Programme',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams) => {
        const programmeType = getProgrammeType(params.value as string);
        const programmeNumber = getProgrammeNumber(params.value as string);
        return (
          <Chip
            label={`${programmeType} ${programmeNumber}`}
            size="small"
            sx={{
              bgcolor: programmeType === 'Max' ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.primary.main, 0.1),
              color: programmeType === 'Max' ? theme.palette.error.main : theme.palette.primary.main,
              fontWeight: 600,
              borderRadius: 2,
            }}
          />
        );
      },
    },
    {
      field: 'health_consult_date',
      headerName: 'Health Consult',
      flex: 1,
      minWidth: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CalendarIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {formatDate(params.value as string)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'first_session_date',
      headerName: 'First Session',
      flex: 1,
      minWidth: 130,
      renderCell: (params: GridRenderCellParams) => {
        const weeks = calculateWeeksSinceFirstSession(params.value as string);
        return (
          <Stack>
            <Typography variant="body2">
              {formatDate(params.value as string)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Week {weeks}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'sessions',
      headerName: 'Sessions',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="PT Sessions">
            <Chip
              icon={<FitnessIcon />}
              label={params.row.pt_sessions}
              size="small"
              sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}
            />
          </Tooltip>
          <Tooltip title="Metabolic Screenings">
            <Chip
              icon={<ScienceIcon />}
              label={params.row.metabolic_screenings}
              size="small"
              sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}
            />
          </Tooltip>
          <Tooltip title="VALD Assessments">
            <Chip
              icon={<SpeedIcon />}
              label={params.row.physical_performance_screenings}
              size="small"
              sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}
            />
          </Tooltip>
          <Tooltip title="Nutrition Sessions">
            <Chip
              icon={<NutritionIcon />}
              label={params.row.nutrition_sessions}
              size="small"
              sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1) }}
            />
          </Tooltip>
        </Stack>
      ),
    },
    {
      field: 'ignition_expiration_date',
      headerName: 'Expiration',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => {
        const weeksLeft = calculateWeeksUntilExpiration(params.value as string);
        const getStatusIcon = () => {
          if (weeksLeft <= 2) return <WarningIcon fontSize="small" />;
          if (weeksLeft <= 4) return <TimerIcon fontSize="small" />;
          return <CheckCircleIcon fontSize="small" />;
        };
        const getStatusColor = () => {
          if (weeksLeft <= 2) return theme.palette.error.main;
          if (weeksLeft <= 4) return theme.palette.warning.main;
          return theme.palette.success.main;
        };
        
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ color: getStatusColor() }}>
              {getStatusIcon()}
            </Box>
            <Stack>
              <Typography variant="body2">
                {formatDate(params.value as string)}
              </Typography>
              <Typography variant="caption" sx={{ color: getStatusColor(), fontWeight: 600 }}>
                {weeksLeft} weeks left
              </Typography>
            </Stack>
          </Stack>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
          <IconButton
            size="small"
            onClick={() => navigate(`/clients/${params.row.client_id}`)}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </motion.div>
      ),
    },
  ];

  // Prepare rows for DataGrid
  const rows = useMemo(() => {
    return filteredClients.map(client => ({
      ...client,
      id: client.client_id, // DataGrid requires 'id' field
    }));
  }, [filteredClients]);

  useEffect(() => {
    fetchIgnitionClients();
  }, [userRole, userMetadata]);

  if (userRole !== 'ADMIN' && userRole !== 'MANAGER' && userRole !== 'USER') {
    return (
      <Box p={3}>
        <Typography variant="h5" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1">
          You do not have permission to view the Ignition Overview.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Ignition Overview
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track and manage all Ignition programme clients
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <AnimatePresence>
              {activeFilters > 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                >
                  <Chip
                    label={`${activeFilters} filter${activeFilters > 1 ? 's' : ''} active`}
                    onDelete={clearFilters}
                    deleteIcon={<ClearIcon />}
                    color="primary"
                    variant="outlined"
                  />
                </motion.div>
              )}
            </AnimatePresence>
            <Typography variant="h6" color="primary">
              {filteredClients.length} clients
            </Typography>
          </Stack>
        </Stack>
      </motion.div>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4, mt: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card 
              elevation={0}
              sx={{ 
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                borderRadius: 3,
                height: '100%',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography color="text.secondary" variant="overline" sx={{ fontWeight: 600 }}>
                      Active Clients
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.success.main, mt: 1 }}>
                      {statistics.active}
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 28 }} />
                  </Box>
                </Box>
              </CardContent>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                }}
              />
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Card 
              elevation={0}
              sx={{ 
                background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.light, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                borderRadius: 3,
                height: '100%',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography color="text.secondary" variant="overline" sx={{ fontWeight: 600 }}>
                      Inactive Clients
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.error.main, mt: 1 }}>
                      {statistics.inactive}
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <TrendingDownIcon sx={{ color: theme.palette.error.main, fontSize: 28 }} />
                  </Box>
                </Box>
              </CardContent>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.error.main, 0.05),
                }}
              />
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <Card 
              elevation={0}
              sx={{ 
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: 3,
                height: '100%',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography color="text.secondary" variant="overline" sx={{ fontWeight: 600 }}>
                      Max Programme
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.primary.main, mt: 1 }}>
                      {statistics.max}
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FireIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                  </Box>
                </Box>
              </CardContent>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                }}
              />
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            <Card 
              elevation={0}
              sx={{ 
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.light, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                borderRadius: 3,
                height: '100%',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography color="text.secondary" variant="overline" sx={{ fontWeight: 600 }}>
                      Total Clients
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.info.main, mt: 1 }}>
                      {statistics.total}
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <GroupsIcon sx={{ color: theme.palette.info.main, fontSize: 28 }} />
                  </Box>
                </Box>
              </CardContent>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                }}
              />
            </Card>
          </motion.div>
        </Grid>
      </Grid>
      
      {/* Enhanced Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card
          sx={{ mb: 3, overflow: 'visible' }}
          elevation={0}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" mb={2}>
            <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6" fontWeight={600}>
              Filters
            </Typography>
          </Stack>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                label="Search Clients"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Location</InputLabel>
                <Select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  label="Location"
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationIcon />
                    </InputAdornment>
                  }
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      transition: 'all 0.2s',
                    },
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <MenuItem value="">All Locations</MenuItem>
                  {availableLocations.map((location) => (
                    <MenuItem key={location} value={location}>
                      {location}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Coach</InputLabel>
                <Select
                  value={coachFilter}
                  onChange={(e) => setCoachFilter(e.target.value)}
                  label="Coach"
                  startAdornment={
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  }
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      transition: 'all 0.2s',
                    },
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <MenuItem value="">All Coaches</MenuItem>
                  {availableCoaches.map((coach) => (
                    <MenuItem key={coach.id} value={coach.id}>
                      {coach.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Programme</InputLabel>
                <Select
                  value={programmeFilter}
                  onChange={(e) => setProgrammeFilter(e.target.value)}
                  label="Programme"
                  startAdornment={
                    <InputAdornment position="start">
                      <CategoryIcon />
                    </InputAdornment>
                  }
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      transition: 'all 0.2s',
                    },
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <MenuItem value="">All Programmes</MenuItem>
                  <MenuItem value="Regular">Regular</MenuItem>
                  <MenuItem value="Max">Max</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
        </Card>
      </motion.div>
      
      {/* Enhanced Data Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Paper
          elevation={0}
          sx={{
            height: 'calc(100vh - 380px)',
            minHeight: 400,
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            '& .MuiDataGrid-root': {
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderBottom: `2px solid ${theme.palette.divider}`,
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                },
              },
              '& .MuiDataGrid-cell': {
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                py: 2,
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                },
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                  },
                },
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: `2px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
              },
            },
          }}
        >
          {loading ? (
            <LoadingContainer loading={loading} minHeight="400px" message="LOADING CLIENTS" />
          ) : error ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%" p={3}>
              <Typography color="error" variant="body1">
                {error}
              </Typography>
            </Box>
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              sortModel={sortModel}
              onSortModelChange={(model) => setSortModel(model)}
              disableRowSelectionOnClick
              autoHeight={false}
              density="comfortable"
              sx={{
                fontSize: '0.875rem',
              }}
              slots={{
                loadingOverlay: () => (
                  <Stack height="100%" alignItems="center" justifyContent="center">
                    <CustomLoader size={50} message="LOADING" />
                  </Stack>
                ),
                noRowsOverlay: () => (
                  <Stack height="100%" alignItems="center" justifyContent="center">
                    <Typography variant="body1" color="text.secondary">
                      No ignition clients found
                    </Typography>
                  </Stack>
                ),
              }}
            />
          )}
        </Paper>
      </motion.div>
    </Box>
  );
};

export default IgnitionOverview;